import { useEffect, useState, useMemo, useCallback } from 'react';
import { Grid, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BOOST_STATE_TYPES } from '../../../constans/parameter';
import useApiService from '../../../component/hooks/useApiService';
import { periodOfferBoostsColumns } from '../../../component/common/tableHelper';
import { apiUrls } from '../../../constans/apiUrls';
import { BoostStatusActions } from '../../../constans/enum';
import { useDialog } from '../../../context/dialog/DialogContext';
import { useAppSnackbar } from '../../../component/providers/SnackbarProvider';
import Filter from '../../../component/ui/filter/filter';
import DataGrid from '../../../component/ui/data-grid/DataGrid';
import CreateBoost from '../CreateBoost';

const PeriodOfferBoosts = () => {
  const [rows, setRows] = useState([]);
  const [documentType] = useState();
  const [filter, setFilter] = useState({});
  const { showSnackbar } = useAppSnackbar();
  const { openDialog } = useDialog();
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

  const handleClick = () => {
    openDialog({
      title: "Period Boost Kuralı Ekle",
      maxWidth: 'sm',
      content: <CreateBoost openType={"Create"} bucket={"period"} />,
      callback: (response) => {
        if (response.variant_id) {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          fetchData();
          showSnackbar('Boost kuralı eklendi.', 'info');
        }
      },
    });

  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { executeGet } = useApiService();
  const columns = useMemo(() => periodOfferBoostsColumns(), []);

  const fetchData = async (filterParams = filter, page = paginationModel.page, pageSize = paginationModel.pageSize) => {
    setLoading(true);
    try {
      const response = await executeGet({
        url: apiUrls.BoostsRule,
        params: {
          ...filterParams,
          offset: page * pageSize,
          bucket: "period",
          limit: pageSize,
        },
      });

      setRows(response.items ?? []);
      setTotalCount(response.meta?.total ?? 0);
    } catch (err) {
      console.error('Veri alınamadı:', err);
      setError('Veri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filter, paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleReset = () => {
    const emptyFilter = {};
    setFilter(emptyFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (paginationModel.page === 0) {
      fetchData({}, 0);
    }
  };

  const onChangeBoostStatusToInactiveClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>
            Boost'un durumu <b>inactive</b>'e çekilecektir. Onaylıyor musunuz?
          </Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            Not: Inactive edilen boost kuralı tekrar active edilemez.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                close(true);
                openDialog({
                  title: 'Durumu güncelle',
                  maxWidth: 'sm',
                  content: (
                    <CreateBoost
                      data={data}
                      openType={'Update'}
                      adminPanelAction={BoostStatusActions.INACTIVE}
                      bucket={'period'}
                    />
                  ),
                  callback: (response) => {
                    if (response?.variant_id) {
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      fetchData();
                      showSnackbar('Boost durumu güncellendi.', 'info');
                    }
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    [filter]
  );

  const onChangeBoostStatusToActiveClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>
            Boost'un durumu <b>active</b>'e çekilecektir. Onaylıyor musunuz?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              onClick={() => {
                close(true);
                openDialog({
                  title: 'Durumu güncelle',
                  maxWidth: 'sm',
                  content: (
                    <CreateBoost
                      data={data}
                      openType={'Update'}
                      adminPanelAction={BoostStatusActions.ACTIVE}
                      bucket={'period'}
                    />
                  ),
                  callback: () => {
                    fetchData(filter);
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    [filter]
  );

  const onViewClicked = useCallback(
    (data) => {
      openDialog({
        title: "Boost Bilgisi",
        maxWidth: 'sm',
        content: <CreateBoost openType={"View"} data={data} bucket={"period"} />,
        callback: () => { fetchData(filter) },
      });
    },
    [filter]);

  const rowActions = useMemo(
    () => [
      {
        name: 'view',
        icon: 'view',
        tooltip: "Görüntüle",
        onClick: onViewClicked
      },
      {
        name: 'StatusActiveToInactiveUpdate',
        icon: "statusUpdate",
        tooltip: "Durumu inactive'e çek",
        onClick: onChangeBoostStatusToInactiveClicked,
      },
      {
        name: 'StatusInactiveToActiveUpdate',
        icon: "statusUpdate",
        tooltip: "Durumu active'e çek",
        onClick: onChangeBoostStatusToActiveClicked,
      }

    ],
    [filter]
  );

  const onRowActionVisible = (row, action) => {
    switch (action) {
      case 'view':
        return true;
      case 'StatusActiveToInactiveUpdate':
        return row.state === "active";
      default:
        return false;
    }
  };

  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <Filter
        onFilter={() => fetchData(filter)}
        onFilterReset={handleReset}
        rightAction={(
          <Button onClick={handleClick} variant="contained" startIcon={<AddIcon />} sx={{
            textTransform: 'none',
            backgroundColor: '#1976d2',
            color: 'white',
            borderRadius: '10px',
            fontWeight: 'bold',
          }}>Yeni Boost Kuralı Ekle</Button>
        )}
      >

        <Grid item xs={4}>
          <FormControl sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel id="boost-state-label">Durum</InputLabel>
            <Select
              labelId="boost-state-label"
              id="boost-state"
              value={filter?.state || ''}
              onChange={(e) =>
                setFilter({ ...filter, state: e.target.value })
              }
              label="Durum"
            >

              {BOOST_STATE_TYPES.map((type) => (
                <MenuItem key={type.Code} value={type.Code}>
                  {type.ValueText1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <DatePicker
              label="Oluşturulma Tarihi"
              value={filter?.LCIssueStartDate ? dayjs(filter.LCIssueStartDate) : null}
              onChange={(e) => {
                setFilter({ ...filter, LCIssueStartDate: e ? e.toISOString() : null });
              }}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: {
                    '& .MuiInputAdornment-root': {
                      marginLeft: '20px',
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid> */}
      </Filter>

      <div style={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          rowActions={rowActions}
          columns={columns}
          onRowActionVisible={onRowActionVisible}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          loading={loading}
          autoHeight
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-cell[data-field="tierQuantities"], & .MuiDataGrid-cell[data-field="perUseCaps"]': {
              alignItems: 'flex-start',
              overflow: 'visible',
              py: 0.5,
            },
            '& .MuiDataGrid-cell[data-field="category_name"], & .MuiDataGrid-cell[data-field="state"], & .MuiDataGrid-cell[data-field="__actions__"], & .MuiDataGrid-cell[data-field="valid_from"], & .MuiDataGrid-cell[data-field="valid_until"]': {
              alignItems: 'flex-start',
            },
          }}
          getRowId={(row, index) => row.variant_id ?? index + 1}
        />
      </div>
    </div>
  );
};

export default PeriodOfferBoosts;
